import { tagged } from "@mirage/service-logging";
import { namespace } from "@mirage/service-operational-metrics";
import { TYPEAHEAD_SEARCH_SECTIONS } from "@mirage/service-typeahead-search/service/sections";
import instrument from "@mirage/service-typeahead-search/service/utils/instrument";
import * as rx from "rxjs";
import * as op from "rxjs/operators";
import { predicateFactory } from "../utils/predicate-factory";
import {
  createTimeBasedSegmentation,
  dedupeRecentsInDefaultMode,
  dedupeResults,
  filterDropboxersOnlyResults,
  finalizeScoredSources,
  getSources,
  limitAndSortBySection,
  postProcessResults,
  sortResultsBySection,
} from "./pipeline";
import { getLimits, intervals, performCacheAnalysis } from "./utils";

import type { TypeaheadCache } from "@mirage/service-typeahead-search/service/typeahead-cache";
import type { typeahead } from "@mirage/service-typeahead-search/service/types";
import type { Observable } from "rxjs";

const logger = tagged("service-typeahead-search/search");
const metrics = namespace("typeahead-search");
export const INSTRUMENT_DEBUG = false;
INSTRUMENT_DEBUG satisfies false;

/**
 * Returns an observable of arrays of typeahead search results
 * It will handle collecting, deduplication, scoring, grouping, limiting, and
 * sorting of the results in each emitted array.
 *
 * There's a diagram of the data flow in confluence:
 * https://dropbox.atlassian.net/wiki/spaces/Dash/pages/1569784549/service-typeahead-search+-+search+function+diagram
 */
export default function search(
  untrimmedQuery: string,
  mode: typeahead.Mode,
  cache: TypeaheadCache,
  auxiliarySources: typeahead.PredicateSubject<typeahead.SourceId>[],
  config: typeahead.Config,
  limit: number = 5,
): Observable<{ effectiveQuery: string; results: typeahead.ScoredResult[] }> {
  return rx.of({ untrimmedQuery, mode, config }).pipe(
    op.switchMap(({ untrimmedQuery, mode, config }) => {
      // sanitize query
      const query = untrimmedQuery.trim();

      // get section limits and total limits
      const [grossLimit, sectionLimit] = getLimits(query, mode, limit, config);

      // time-based cutoffs aren't likely to be immediately relevant, but this gives us
      // future optionality for async sources that take time to emit
      const [cutoff, end] = intervals([50, 1_000]);

      // analyze cache (quicky!)
      const cacheAnalysis = performCacheAnalysis(cache);

      // derive potentially visible sections
      const callableSections = predicateFactory<typeahead.SectionId>(
        untrimmedQuery,
        mode,
        config.isOmniboxEnabled ? TYPEAHEAD_SEARCH_SECTIONS : [],
        {
          config,
          cacheAnalysis,
        },
      );

      // source results, and add to sections, based on current state
      const sources$ = rx
        .from(callableSections)
        .pipe(
          op.toArray(),
          op.switchMap((sectionsArray) =>
            getSources(
              query,
              mode,
              {
                cache,
                sections: sectionsArray,
                config,
                grossLimit,
                sectionLimit,
              },
              auxiliarySources,
              cacheAnalysis,
            ),
          ),
        )
        .pipe(filterDropboxersOnlyResults(config));

      // instrument latency of source aggregation
      const isources$ = instrument(
        sources$,
        (stats) => {
          if (INSTRUMENT_DEBUG) logger.debug(`sources`, stats);
          metrics.stats("search/sources", stats.duration, {
            name: "sources",
          });
        },
        true,
      );

      // dedupe based on UUID across all sources
      const dedupedSources$ = isources$.pipe(dedupeResults());

      // dedupe recent-chats and previous-queries when in default mode
      const dedupedSourcesInDefaultMode$ = dedupedSources$.pipe(
        dedupeRecentsInDefaultMode(mode),
      );

      // instrument latency of dedupe
      const idedupedSources$ = instrument(
        dedupedSourcesInDefaultMode$,
        (stats) => {
          if (INSTRUMENT_DEBUG) logger.debug(`dedupe`, stats);
          metrics.stats("search/dedupe", stats.duration, {
            name: "dedupe",
          });
        },
        true,
      );

      // limits the number of sources to what will actually be visible
      const limitedAndSortedSources$ = idedupedSources$.pipe(
        limitAndSortBySection(grossLimit, sectionLimit, mode, config),
      );

      // applies section scoring to the limited sources
      const finalizedScoredSources$ = limitedAndSortedSources$.pipe(
        finalizeScoredSources(query, mode, callableSections, cache, config),
      );

      // instrument latency of scoring
      const ifinalizedScoredSources$ = instrument(
        finalizedScoredSources$,
        (stats) => {
          if (INSTRUMENT_DEBUG) logger.debug(`scored`, stats);
          metrics.stats("search/scoring", stats.duration, {
            name: "scoring",
          });
        },
        true,
      );

      // finalizes a sort within sections
      const sortedSourcesInSections$ = ifinalizedScoredSources$.pipe(
        sortResultsBySection(config.isOmniboxEnabled),
      );

      // instrument latency of sorting
      const isortedSourcesInSections$ = instrument(
        sortedSourcesInSections$,
        (stats) => {
          if (INSTRUMENT_DEBUG) logger.debug(`sorted`, stats);
          metrics.stats("search/sorting", stats.duration, {
            name: "sorting",
          });
        },
        true,
      );

      // group results by type into tiers
      const postProcessedSourcesInSections$ = isortedSourcesInSections$.pipe(
        postProcessResults(query, grossLimit, config),
      );

      // instrument latency of grouping/tier logic
      const ipostProcessedSourcesInSections$ = instrument(
        postProcessedSourcesInSections$,
        (stats) => {
          if (INSTRUMENT_DEBUG) logger.debug(`postProcessed`, stats);
          metrics.stats("search/grouping", stats.duration, {
            name: "grouping",
          });
        },
        true,
      );

      // perform result segmentation to prevent killing the UI with too many
      // intermediate result states and re-renders
      const segmentation$ = ipostProcessedSourcesInSections$.pipe(
        createTimeBasedSegmentation(cutoff, end),
      );

      // instrument latency of segmentation logic
      const isegmentation$ = instrument(
        segmentation$,
        (stats) => {
          if (INSTRUMENT_DEBUG) logger.debug(`segmented`, stats);
          metrics.stats("search/segmentation", stats.duration, {
            name: "segmentation",
          });
        },
        true,
      );

      const results$ = instrument(
        isegmentation$,
        (stats) => {
          if (INSTRUMENT_DEBUG) logger.debug(`typeahead search`, stats);
          // it's possible we did not emit any results, do not pollute our ttfr values
          if (stats.first !== -1) metrics.stats("search/ttfr", stats.first);
          metrics.stats("search/duration", stats.duration);
          metrics.counter("search/status", 1, {
            status: stats.failed ? "error" : "success",
          });
        },
        true,
      );

      const resultsWrapper = (results: typeahead.ScoredResult[]) => ({
        effectiveQuery: query,
        results,
      });

      return results$.pipe(op.map(resultsWrapper));
    }),
  );
}
